import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Chip, ListSubheader } from "@mui/material";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { SvgAssets } from "../../../../components/icon/assets";
import Icon from "../../../../components/icon/icon";
import { ONBOARDING_PATH, ROOT_PATH } from "../../../../routes/constants";
import { useQuery } from "../../../../utils/hooks/use-query";
import {
  ExamStatus,
  LicenseStatus,
  OnboardingExamRegistryStatus,
  RegistrationApprovalStatus,
  RegistrationStatus,
} from "../../types";
import { completeBoxStyle, completeIconStyle, item, subheader } from "./style";
import { ModuleSection, SectionItem } from "./type";
import { useTranslation } from "react-i18next";
import { color } from "../../../../mui-theme/palette";
import { TakeUIClickEventParameters } from "../../../../common/ga/types/ga-general-type";
import { takeUIClickEvent } from "../../../../common/ga/ga";

type MenuListProps = {
  sections?: ModuleSection[];
};

export const MenuList = (props: MenuListProps) => {
  const { sections = [] } = props;
  const navigate = useNavigate();
  const query = useQuery();
  const { t } = useTranslation();

  const getItemClickHandler = useCallback(
    (d: SectionItem) => {
      if (
        [OnboardingExamRegistryStatus.FAILED, OnboardingExamRegistryStatus.PASSED].includes(
          d.status as any,
        )
      ) {
        navigate(
          `${ROOT_PATH}/${ONBOARDING_PATH}/exams/registry/${
            d.data.id
          }/feedback?${query.toString()}`,
          {
            state: { exam: d.data },
          },
        );
      } else {
        navigate(
          `${ROOT_PATH}/${ONBOARDING_PATH}/exams/registry/${d.data.id}?${query.toString()}`,
          {
            state: { data: d.data },
          },
        );
      }
      const gaData: TakeUIClickEventParameters = {
        module: "Recruitment",
        feature: "License",
        journey: "view_individual_license",
        stage: "select_individual_license",
        screen_id: "SCR_RCRTMT_LCNS_MN",
        screen_name: "Recruitment-License-Menu-Screen",
        object_name: "IndividualLicense",
        object_type: "BUTTON",
      };
      takeUIClickEvent(gaData);
    },
    [navigate, query],
  );

  const convertData = useCallback(
    (item: SectionItem) => {
      const examName = item.title;

      const {
        registrationApprovalStates: registrationApprovalStatus,
        licenseApprovalStates: licenseStatus,
        registrationStatus,
      } = item?.data?.originData;
      let examStatus = item?.data?.examResult;
      if (registrationStatus === RegistrationStatus.TIMESLOT_CANCELLED) {
        examStatus = ExamStatus.CANCELLED;
      }

      if (
        registrationStatus === RegistrationStatus.NOT_STARTED &&
        (registrationApprovalStatus === RegistrationApprovalStatus.NOT_STARTED ||
          !registrationApprovalStatus) &&
        (examStatus === ExamStatus.NOT_STARTED || !examStatus) &&
        (licenseStatus === LicenseStatus.NOT_STARTED || !licenseStatus)
      ) {
        return {
          ...item,
          examName,
          statusI18nKey: t("Common.NOT_STARTED"),
          status: "NOT_STARTED",
          statusDesc: t("Recruitment.complete_module_now"),
        };
      }

      if (licenseStatus === LicenseStatus.PENDING_APPROVAL) {
        return {
          ...item,
          examName,
          statusI18nKey: t("Common.IN_PROGRESS"),
          status: "IN_PROGRESS",
          statusDesc: t("Recruitment.exam_proof_request_review"),
        };
      }

      if (licenseStatus === LicenseStatus.RETURNED) {
        return {
          ...item,
          examName,
          statusI18nKey: t("Common.IN_PROGRESS"),
          status: "IN_PROGRESS",
          statusDesc: t("Recruitment.verification_returned_review_info"),
        };
      }

      if ([LicenseStatus.CANCELLED, LicenseStatus.REJECTED].includes(licenseStatus)) {
        return {
          ...item,
          examName,
          statusI18nKey: t("Common.IN_PROGRESS"),
          status: "IN_PROGRESS",
          statusDesc: t("Recruitment.verification_cancelled_restart"),
        };
      }

      if (licenseStatus === LicenseStatus.APPROVED) {
        return {
          ...item,
          examName,
          statusI18nKey: t("Common.COMPLETED"),
          status: "COMPLETED",
          statusDesc: t("Recruitment.exam_proof_request_passed_verification"),
        };
      }

      if (registrationApprovalStatus === RegistrationApprovalStatus.PENDING_APPROVAL) {
        const timeslot = `${item?.data?.originData?.examSchedule?.examDate},${item?.data?.originData?.examSchedule?.startTime}-${item?.data?.originData?.examSchedule?.endTime}`;
        const statusDesc = t("Recruitment.registered_timeslot", { timeslot });
        return {
          ...item,
          examName,
          statusI18nKey: t("Common.IN_PROGRESS"),
          status: "IN_PROGRESS",
          statusDesc,
        };
      }

      if (registrationApprovalStatus === RegistrationApprovalStatus.RETURNED) {
        return {
          ...item,
          examName,
          statusI18nKey: t("Common.IN_PROGRESS"),
          status: "IN_PROGRESS",
          statusDesc: t("Recruitment.registration_returned_review_info"),
        };
      }

      if (
        registrationApprovalStatus === RegistrationApprovalStatus.CANCELLED ||
        registrationApprovalStatus === RegistrationApprovalStatus.REJECTED
      ) {
        return {
          ...item,
          examName,
          statusI18nKey: t("Common.IN_PROGRESS"),
          status: "IN_PROGRESS",
          statusDesc: t("Recruitment.registration_cancelled_restart"),
        };
      }

      if (examStatus === ExamStatus.CANCELLED) {
        return {
          ...item,
          examName,
          statusI18nKey: t("Common.IN_PROGRESS"),
          status: "IN_PROGRESS",
          statusDesc: t("Recruitment.exam_cancelled_register_another_timeslot"),
        };
      }

      if (examStatus === ExamStatus.FAIL) {
        return {
          ...item,
          examName,
          statusI18nKey: t("Common.IN_PROGRESS"),
          status: "IN_PROGRESS",
          statusDesc: t("Recruitment.exam_failed_register_again"),
        };
      }

      if (
        [RegistrationApprovalStatus.REGISTERED, RegistrationApprovalStatus.COMPLETED].includes(
          registrationApprovalStatus,
        ) &&
        (examStatus === ExamStatus.NOT_STARTED || !examStatus)
      ) {
        const timeslot = `${item?.data?.originData?.examSchedule?.examDate},${item?.data?.originData?.examSchedule?.startTime}-${item?.data?.originData?.examSchedule?.endTime}`;
        const statusDesc = t("Recruitment.registered_timeslot", { timeslot });
        return {
          ...item,
          examName,
          statusI18nKey: t("Common.COMPLETED"),
          status: "COMPLETED",
          statusDesc,
        };
      }

      if (examStatus === ExamStatus.PASS) {
        return {
          ...item,
          examName,
          statusI18nKey: t("Common.COMPLETED"),
          status: "COMPLETED",
          statusDesc: t("Recruitment.exam_passed_congratulations"),
        };
      }
      return {
        ...item,
        examName,
        statusI18nKey: "Unknown",
        status: "Unknown",
        statusDesc: t("Recruitment.status_info_not_available"),
      };
    },
    [t],
  );

  return (
    <div className="px-4">
      <List>
        {sections.map((section, index) => {
          return (
            <div key={index}>
              <ListSubheader sx={subheader}>
                <ListItemText primary={section.title} />
              </ListSubheader>
              <List>
                {section.data.map((d, subIndex) => {
                  const data = convertData(d);
                  return (
                    <div key={`${index}-${subIndex}`} className="bg-white mb-3 rounded-xl">
                      <ListItemButton
                        sx={{ backgroundColor: "white" }}
                        onClick={() => getItemClickHandler(d)}
                      >
                        <ListItemText
                          sx={item}
                          primary={data?.title}
                          secondary={
                            data.status !== RegistrationStatus.NOT_STARTED ? data?.statusDesc : ""
                          }
                        />
                        {data.status !== RegistrationStatus.NOT_STARTED && (
                          <Chip
                            label={data?.statusI18nKey}
                            size="small"
                            sx={{
                              backgroundColor:
                                data.status === RegistrationStatus.IN_PROGRESS
                                  ? color.cyanLight
                                  : color.greenLight,
                              color:
                                data.status === RegistrationStatus.IN_PROGRESS
                                  ? color.cyan
                                  : color.green,
                            }}
                          />
                        )}

                        <StatusIcon status={d.status}></StatusIcon>
                      </ListItemButton>
                    </div>
                  );
                })}
              </List>
            </div>
          );
        })}
      </List>
    </div>
  );
};

const StatusIcon = ({ status }: { status?: keyof typeof OnboardingExamRegistryStatus }) => {
  switch (status) {
    case OnboardingExamRegistryStatus.REGISTERED:
    case OnboardingExamRegistryStatus.PASSED:
      return (
        <Box sx={completeBoxStyle}>
          <CheckIcon sx={completeIconStyle} />
        </Box>
      );
    case OnboardingExamRegistryStatus.FAILED:
      return <Icon svg={SvgAssets.failure} height={32} width={32} />;
    default:
      return <ChevronRightIcon />;
  }
  return <></>;
};
